import * as UI from "@iyk/ui"
import { Drawer as BaseDrawer } from "vaul-base"
import { ScrollArea } from "./ScrollArea.tsx"

const Root = ({ children, ...props }: Drawer.Root.Props) => {
  return <BaseDrawer.Root {...props}>{children}</BaseDrawer.Root>
}

const Content = ({ children, dismissible = true, ...props }: Drawer.Content.Props) => {
  return (
    <BaseDrawer.Portal>
      <BaseDrawer.Overlay
        data-slot="drawer-overlay"
        className="fixed inset-0 bg-[rgba(25,25,25,0.8)] backdrop-blur transition-all duration-200"
      />

      <BaseDrawer.Content
        data-slot="drawer-content"
        className={UI.cx(
          "group/drawer-content outline-none",
          "fixed inset-x-0 bottom-0 z-dialog overflow-hidden",
          "grid grid-rows-[1.75rem_minmax(0,1fr)] max-h-[calc(100dvh-2rem)] ",
          "bg-gray-1 text-gray-12",
          "rounded-t-xl shadow-dialog"
        )}
        {...props}
      >
        <div className="flex items-center justify-center h-7 shrink-0 bg-gray-1 z-50">
          {dismissible && <span className="block bg-gray-5 rounded-full h-1 w-16" />}
        </div>
        <ScrollArea className="h-full min-h-0 shrink-0">
          <div className="flex flex-col gap-4 p-4 pt-0">{children}</div>
        </ScrollArea>
      </BaseDrawer.Content>
    </BaseDrawer.Portal>
  )
}

const Header = ({ children }: Drawer.Header.Props) => {
  return <div className="flex flex-col gap-0.5">{children}</div>
}

const Title = ({ children }: Drawer.Title.Props) => {
  return <h2 className="text-sm font-medium text-gray-12">{children}</h2>
}

const Description = ({ children }: Drawer.Description.Props) => {
  return <p className="text-sm text-gray-11">{children}</p>
}

export namespace Drawer {
  export namespace Root {
    export type Props = React.ComponentProps<typeof BaseDrawer.Root>
  }

  export namespace Content {
    export type Props = Omit<React.ComponentProps<typeof BaseDrawer.Content>, "className" | "data-slot"> & {
      dismissible?: boolean
    }
  }

  export namespace Header {
    export type Props = {
      children: React.ReactNode
    }
  }

  export namespace Title {
    export type Props = {
      children: React.ReactNode
    }
  }

  export namespace Description {
    export type Props = {
      children: React.ReactNode
    }
  }
}

export const Drawer = {
  Root,
  Trigger: BaseDrawer.Trigger,
  Content,
  Close: BaseDrawer.Close,
  Header,
  Title,
  Description
}
